/* istanbul ignore file */

import { useMemo } from 'react';
import { useLocalSearchParams } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';

import { useMachine } from '@xstate5/react';
import { useClient } from 'urql';

import { useNoticeBannersStackContext } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { useLastInteractedStore } from '../../../../context/LastInteractedStore';
import { locationSearchMachine } from '../../machine';
import { useLocationSearchNavigation } from '../useLocationSearchNavigation';
import { useLocationSearchTelemetry } from '../useLocationSearchTelemetry';

import type {
  BaseLocationRadiusConfig,
  LocationSearchMachineContext,
  LocationSearchMachineEvents,
  LocationsSearchType,
} from '../../machine';

// ─────────────────────────────────────────────────────────────────────────────

export const useInitLocationSearchMachine = (props: UseInitLocationSearchMachine) => {
  const {
    isInsideModal,
    navigateToLocation,
    navigateToDeliveryLocation,
    shouldRequestLocationPermission,
  } = props;

  const client = useClient();
  const routeParams = useLocalSearchParams<{ orderChannel?: string }>();

  const lastInteractedStore = useLastInteractedStore();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { formatMessage } = useIntl();
  const locationSearchNavigation = useLocationSearchNavigation({ isInsideModal });
  const {
    trackUserGeolocation,
    trackOrderChannelSelected,
    setOrderChannelProperty,
    trackLocationSearchByArea,
    trackDeliveryLocationResolved,
    trackFocusedLocationPinId,
    trackNavigateToLocationMenu,
    trackNavigateToDeliveryLocationMenu,
    trackSearchPredictionsAndRestaurantsError,
    trackSearchForPredictionsAndRestaurants,
    trackDeliveryLocationError,
  } = useLocationSearchTelemetry();
  const isSignedIn = useIsLoggedIn();

  // ─────────────────────────────────────────────────────────────────────

  const initialOrderChannel = getInitialLocationSearchType(routeParams.orderChannel); // prettier-ignore

  // ─── Flags ───────────────────────────────────────────────────────────

  const baseLocationRadiusConfig = useFeatureFlag('CELS-4859-base-location-radius-config');

  // ─────────────────────────────────────────────────────────────────────

  const lastInteractedStoreAddressId = lastInteractedStore?.addressId;

  // ─── Machine ─────────────────────────────────────────────────────────

  const machine = useMemo(
    () =>
      locationSearchMachine.provide({
        actions: {
          navigateToLocation: ({ context }) => {
            (navigateToLocation ?? locationSearchNavigation.navigateToLocation)(context);
          },
          navigateToDeliveryLocation: ({ context }) => {
            (navigateToDeliveryLocation ?? locationSearchNavigation.navigateToDeliveryLocation)(
              context,
            );
          },

          // Fires on the guarded `SEARCH_BY_CURRENT_POSITION` branch, where
          // `context.currentPosition` is guaranteed resolved.
          onUserLocationChanged: ({ context }) => {
            if (!context.currentPosition) return;

            trackUserGeolocation({ coords: context.currentPosition });
          },
          onOrderChannelResolved: ({ context }) => {
            setOrderChannelProperty(context);

            locationSearchNavigation.setOrderChannelParams(context.locationSearchType); // prettier-ignore
          },
          onOrderChannelSelected: (_args, params) => {
            trackOrderChannelSelected(params.orderChannel);
          },
          onNavigateToLocationEvent: ({ context, event }) => {
            trackNavigateToLocationMenu(getEntryPoint(event))(context);
          },
          onNavigateToDeliveryLocationEvent: ({ context, event }) => {
            trackNavigateToDeliveryLocationMenu(getEntryPoint(event))(context);
          },
          onLocationWarningConfirmEvent: ({ context }) => {
            trackNavigateToLocationMenu('location-warning')(context);
          },
          onSearchByAreaEvent: ({ context }) => {
            trackLocationSearchByArea(context);
          },
          onDeliveryLocationEvent: ({ context }) => {
            trackDeliveryLocationResolved(context);
          },
          onFocusedPinIdChangeEvent: ({ context }) => {
            trackFocusedLocationPinId(context);
          },
          onCurrentPositionChanged: (_args, params) => {
            if (!params?.currentPositionCoordinates) return;

            trackUserGeolocation({ coords: params.currentPositionCoordinates });
          },
          onSearchForPredictions: () => {
            trackSearchForPredictionsAndRestaurants();
          },
          onSearchPredictionsAndRestaurantsError: () => {
            trackSearchPredictionsAndRestaurantsError();

            addNoticeBanner(
              {
                id: 'location-search.notice_banner.predictions-and-restaurants-error',
                text: formatMessage(messages.predictionsAndRestaurantsError),
                palette: 'caution',
              },
              true,
            );
          },
          onDeliveryLocationError: () => {
            trackDeliveryLocationError();

            addNoticeBanner(
              {
                id: 'location-search.notice_banner.delivery-location-error',
                text: formatMessage(messages.deliveryLocationError),
                palette: 'caution',
              },
              true,
            );
          },
          onSearchByCurrentPositionError: () => {
            addNoticeBanner(
              {
                id: 'location-search.notice_banner.search-by-current-position-error',
                text: formatMessage(messages.searchByCurrentPositionError),
                palette: 'neutral',
              },
              true,
            );
          },
          onSearchByAreaError: () => {
            addNoticeBanner(
              {
                id: 'location-search.notice_banner.search-by-area-error',
                text: formatMessage(messages.genericLocationSearchError),
                palette: 'caution',
              },
              true,
            );
          },
          onCheckForLocationWarningError: () => {
            addNoticeBanner(
              {
                id: 'location-search.notice_banner.check-for-location-warning-error',
                text: formatMessage(messages.genericLocationSearchError),
                palette: 'caution',
              },
              true,
            );
          },
          onSearchDeliveryAddressesError: () => {
            addNoticeBanner(
              {
                id: 'location-search.notice_banner.search-delivery-addresses-error',
                text: formatMessage(messages.searchDeliveryAddressesError),
                palette: 'caution',
              },
              true,
            );
          },
          onAddDeliveryAddressIdError: () => {
            addNoticeBanner(
              {
                id: 'location-search.notice_banner.add-delivery-address-error',
                text: formatMessage(messages.addDeliveryAddressError),
                palette: 'caution',
              },
              true,
            );
          },
        },
        guards: {
          checkIfSignedIn: () => isSignedIn,
          checkIfShouldShowDeliveryAddressForm: ({ context }) =>
            !context.deliveryLocation?.addressId,
        },
      }),
    [
      addNoticeBanner,
      formatMessage,
      isSignedIn,
      locationSearchNavigation,
      navigateToDeliveryLocation,
      navigateToLocation,
      trackDeliveryLocationError,
      trackFocusedLocationPinId,
      trackLocationSearchByArea,
      trackDeliveryLocationResolved,
      trackOrderChannelSelected,
      setOrderChannelProperty,
      trackNavigateToDeliveryLocationMenu,
      trackNavigateToLocationMenu,
      trackSearchForPredictionsAndRestaurants,
      trackSearchPredictionsAndRestaurantsError,
      trackUserGeolocation,
    ],
  );

  const input = useMemo(
    () => ({
      client,
      lastInteractedStoreAddressId,
      shouldRequestLocationPermission,
      locationSearchType: initialOrderChannel,
      baseLocationRadiusConfig: getBaseLocationRadiusConfig(baseLocationRadiusConfig), // prettier-ignore
    }),
    [
      baseLocationRadiusConfig,
      client,
      initialOrderChannel,
      lastInteractedStoreAddressId,
      shouldRequestLocationPermission,
    ],
  );

  return useMachine(machine, { input });
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  predictionsAndRestaurantsError: {
    defaultMessage:
      'Something went wrong while searching for locations, try again in a few moments...',
    description: 'Location Search | Predictions and Restaurants Error',
  },
  deliveryLocationError: {
    defaultMessage:
      'Something went wrong while searching for the delivery location. Please try again in a few moments or use a different address.',
    description: 'Location Search | Delivery Location Error',
  },
  searchByCurrentPositionError: {
    defaultMessage:
      'Something went wrong while getting your current position. Please check your location permissions and availability, and try again.',
    description: 'Location Search | Get Current Position Error',
  },
  genericLocationSearchError: {
    defaultMessage: 'Something went wrong. Please try again in a few moments.',
    description: 'Location Search | Generic Error',
  },
  addDeliveryAddressError: {
    defaultMessage: 'Failed to add your delivery address. Please try again in a few moments.',
    description: 'Location Search | Add Delivery Address Error',
  },
  searchDeliveryAddressesError: {
    defaultMessage:
      'Failed to load delivery address suggestions. Please try again in a few moments.',
    description: 'Location Search | Search Delivery Addresses Error',
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitialLocationSearchType(
  initialOrderChannel: string | undefined,
): LocationsSearchType | undefined {
  if (initialOrderChannel === 'delivery') return 'delivery';
  if (initialOrderChannel === 'outpost') return 'outpost';
  if (initialOrderChannel === 'pickup') return 'pickup';
}

function getBaseLocationRadiusConfig(
  maybeConfig: Record<string, number>,
): BaseLocationRadiusConfig {
  return {
    SMALLEST: typeof maybeConfig.SMALLEST === 'number' ? maybeConfig.SMALLEST : 3, // prettier-ignore
    SMALL: typeof maybeConfig.SMALL === 'number' ? maybeConfig.SMALL : 7,
    MEDIUM: typeof maybeConfig.MEDIUM === 'number' ? maybeConfig.MEDIUM : 15,
    LARGE: typeof maybeConfig.LARGE === 'number' ? maybeConfig.LARGE : 40,
    XLARGE: typeof maybeConfig.XLARGE === 'number' ? maybeConfig.XLARGE : 200,
  };
}

function getEntryPoint(event: LocationSearchMachineEvents) {
  return 'entryPoint' in event ? event.entryPoint : undefined;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type UseInitLocationSearchMachine = {
  isInsideModal?: boolean;

  /*
    Optional actions that initiates the navigation process for the selected location.
    When those properties are not provided, the standard navigation is used.

    Helpful when a custom navigation method is utilized or when other actions
    need to be performed before navigation.
   */

  navigateToLocation?: (context: LocationSearchMachineContext) => void;
  navigateToDeliveryLocation?: (context: LocationSearchMachineContext) => void;

  /**
   * When `false`, the machine skips the setup phase that would otherwise
   * trigger the OS location permission request. Defaults to `true`.
   */
  shouldRequestLocationPermission?: boolean;
};
